import Phaser from 'phaser';
import type { FruitKind } from '../types';

export function makeFruit(
  scene: Phaser.Scene,
  kind: FruitKind,
  x: number,
  y: number,
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  switch (kind) {
    case 'apple': {
      c.add(scene.add.circle(0, 0, 16, 0xd62828));
      c.add(scene.add.circle(-5, -5, 5, 0xff7a7a));
      c.add(scene.add.rectangle(2, -16, 3, 8, 0x4a2a10));
      const leaf = scene.add.ellipse(8, -16, 12, 6, 0x2d8a2d);
      leaf.rotation = -0.5;
      c.add(leaf);
      break;
    }
    case 'berry': {
      c.add(scene.add.circle(-6, 2, 9, 0x6b3aa8));
      c.add(scene.add.circle(6, 2, 9, 0x4d228a));
      c.add(scene.add.circle(0, -6, 9, 0x8a4ec2));
      c.add(scene.add.circle(-3, -6, 2, 0xd6b8f5));
      c.add(scene.add.rectangle(0, -14, 3, 6, 0x3a7d2a));
      break;
    }
    case 'melon': {
      // Watermelon slice: green rind + white pith + red flesh + black seeds.
      // A semicircular slice (top half flat).
      const rind = scene.add.graphics();
      rind.fillStyle(0x2c8a3a, 1);
      rind.slice(0, 4, 22, Phaser.Math.DegToRad(180), Phaser.Math.DegToRad(360), true);
      rind.fillPath();
      c.add(rind);
      const pith = scene.add.graphics();
      pith.fillStyle(0xfaf3e1, 1);
      pith.slice(0, 4, 18, Phaser.Math.DegToRad(180), Phaser.Math.DegToRad(360), true);
      pith.fillPath();
      c.add(pith);
      const flesh = scene.add.graphics();
      flesh.fillStyle(0xe05a4a, 1);
      flesh.slice(0, 4, 15, Phaser.Math.DegToRad(180), Phaser.Math.DegToRad(360), true);
      flesh.fillPath();
      c.add(flesh);
      // Dark stripes on the rind
      const stripeG = scene.add.graphics();
      stripeG.fillStyle(0x1f5d2a, 1);
      for (const a of [-1.4, -0.8, -0.2, 0.4, 1.0]) {
        const x = Math.cos(a) * 20;
        const y = 4 + Math.sin(a) * 20;
        stripeG.fillRect(x - 1, y - 1, 2, 4);
      }
      c.add(stripeG);
      // Seeds — sit on the red flesh, below the flat top.
      c.add(scene.add.ellipse(-8, 8, 2.5, 4, 0x111111));
      c.add(scene.add.ellipse(0, 6, 2.5, 4, 0x111111));
      c.add(scene.add.ellipse(8, 8, 2.5, 4, 0x111111));
      c.add(scene.add.ellipse(-4, 12, 2.5, 4, 0x111111));
      c.add(scene.add.ellipse(4, 12, 2.5, 4, 0x111111));
      break;
    }
    case 'pear': {
      c.add(scene.add.ellipse(0, 4, 22, 28, 0xc9d63a));
      c.add(scene.add.ellipse(0, -10, 14, 16, 0xc9d63a));
      c.add(scene.add.rectangle(0, -20, 3, 6, 0x4a2a10));
      const leaf = scene.add.ellipse(6, -20, 10, 5, 0x3a7d2a);
      leaf.rotation = -0.4;
      c.add(leaf);
      break;
    }
    case 'banana': {
      // Banana skin (collectible) — yellow curved peel with brown ends.
      const peel = scene.add.graphics();
      peel.fillStyle(0xf2c14e, 1);
      peel.beginPath();
      peel.arc(0, 8, 18, Phaser.Math.DegToRad(200), Phaser.Math.DegToRad(340), false);
      peel.lineTo(14, 4);
      peel.arc(0, 12, 14, Phaser.Math.DegToRad(340), Phaser.Math.DegToRad(200), true);
      peel.closePath();
      peel.fillPath();
      // Highlight stripe along the peel
      peel.lineStyle(1.5, 0xfae28a, 1);
      peel.beginPath();
      peel.arc(0, 9, 16, Phaser.Math.DegToRad(220), Phaser.Math.DegToRad(320), false);
      peel.strokePath();
      c.add(peel);
      // Brown stem tips
      c.add(scene.add.circle(-15, 0, 2.5, 0x6b3410));
      c.add(scene.add.circle(15, 0, 2.5, 0x6b3410));
      break;
    }
    case 'golden_apple': {
      const glow = scene.add.circle(0, 0, 22, 0xffe066, 0.4);
      const apple = scene.add.circle(0, 0, 16, 0xf2c14e);
      const highlight = scene.add.circle(-5, -5, 5, 0xfff2a8);
      const stem = scene.add.rectangle(2, -16, 3, 8, 0x4a2a10);
      const leaf = scene.add.ellipse(8, -16, 12, 6, 0x2d8a2d);
      leaf.rotation = -0.5;
      c.add([glow, apple, highlight, stem, leaf]);
      scene.tweens.add({
        targets: glow,
        scale: 1.2,
        alpha: 0.7,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      break;
    }
  }
  return c;
}

export function fruitDisplayName(kind: FruitKind): string {
  switch (kind) {
    case 'banana':
      return 'Banana Skins';
    case 'apple':
      return 'Apples';
    case 'berry':
      return 'Berries';
    case 'melon':
      return 'Melons';
    case 'pear':
      return 'Pears';
    case 'golden_apple':
      return 'Golden Apples';
  }
}
